import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { FileSearch, Plus, Trash2, CheckCircle2, Eye } from "lucide-react";

const STATUS_LABELS = {
  open: { label: "Aberta", cls: "bg-amber-500/20 text-amber-600" },
  quoted: { label: "Com Cotações", cls: "bg-blue-500/20 text-blue-500" },
  converted: { label: "Convertida", cls: "bg-emerald-500/20 text-emerald-600" },
};

export default function RequisitionsPage() {
  const { token } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([{ product_id: "", name: "", quantity: "", est_unit_cost: "" }]);
  const [notes, setNotes] = useState("");
  const [quoteForm, setQuoteForm] = useState({ supplier_id: "", total: "", delivery_days: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [r, s, p] = await Promise.all([
        axios.get(`${API_URL}/purchase-requisitions`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/suppliers`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
      ]);
      setReqs(r.data); setSuppliers(s.data); setProducts(p.data);
      if (selected) {
        const updated = r.data.find((x) => x.requisition_id === selected.requisition_id);
        if (updated) setSelected(updated);
      }
    } catch { toast.error("Erro ao carregar requisições"); }
  };

  const createReq = async (e) => {
    e.preventDefault();
    const validItems = items.filter((i) => i.name && i.quantity).map((i) => ({
      product_id: i.product_id || null,
      name: i.name,
      quantity: parseFloat(i.quantity),
      est_unit_cost: toBaseCurrency(i.est_unit_cost),
    }));
    if (validItems.length === 0) { toast.error("Adicione pelo menos um item"); return; }
    try {
      await axios.post(`${API_URL}/purchase-requisitions`, { items: validItems, notes }, { headers, withCredentials: true });
      toast.success("Requisição criada");
      setCreateModal(false); setItems([{ product_id: "", name: "", quantity: "", est_unit_cost: "" }]); setNotes("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const addQuote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/purchase-requisitions/${selected.requisition_id}/quotations`, {
        supplier_id: quoteForm.supplier_id,
        total: toBaseCurrency(quoteForm.total),
        delivery_days: quoteForm.delivery_days ? parseInt(quoteForm.delivery_days) : null,
      }, { headers, withCredentials: true });
      toast.success("Cotação adicionada");
      setQuoteForm({ supplier_id: "", total: "", delivery_days: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const selectQuote = async (q) => {
    if (!window.confirm(`Selecionar cotação de ${q.supplier_name} (${formatCurrency(q.total)}) e criar encomenda?`)) return;
    try {
      const res = await axios.put(`${API_URL}/purchase-requisitions/${selected.requisition_id}/select/${q.quotation_id}`, {}, { headers, withCredentials: true });
      toast.success(`Encomenda ${res.data.number} criada`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteReq = async (r) => {
    if (!window.confirm(`Remover requisição ${r.number}?`)) return;
    try {
      await axios.delete(`${API_URL}/purchase-requisitions/${r.requisition_id}`, { headers, withCredentials: true });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateItem = (i, field, value) => {
    const arr = [...items];
    arr[i][field] = value;
    if (field === "product_id") {
      const p = products.find((x) => x.product_id === value);
      if (p) { arr[i].name = p.name; arr[i].est_unit_cost = arr[i].est_unit_cost || (p.cost_price || ""); }
    }
    setItems(arr);
  };

  const bestQuote = selected?.quotations?.length ? Math.min(...selected.quotations.map((q) => q.total)) : null;

  return (
    <div className="space-y-6" data-testid="requisitions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Requisições & Cotações</h1>
          <p className="text-muted-foreground">Requisição → cotações de fornecedores → comparação → encomenda</p>
        </div>
        <Button onClick={() => setCreateModal(true)} data-testid="add-requisition-btn"><Plus className="w-4 h-4 mr-2" /> Nova Requisição</Button>
      </div>

      {reqs.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><FileSearch className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma requisição de compra</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {reqs.map((r) => (
            <Card key={r.requisition_id} data-testid={`requisition-${r.requisition_id}`}>
              <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-mono font-semibold">{r.number}</p>
                  <p className="text-xs text-muted-foreground">{r.items?.length || 0} item(ns) · {formatDate(r.created_at)} · por {r.created_by_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-sm">Est.: {formatCurrency(r.est_total)}</p>
                  <Badge className={STATUS_LABELS[r.status]?.cls}>{STATUS_LABELS[r.status]?.label || r.status}</Badge>
                  {r.purchase_number && <Badge variant="outline">{r.purchase_number}</Badge>}
                  <Button size="sm" variant="outline" onClick={() => { setSelected(r); setDetailModal(true); }} data-testid={`view-requisition-${r.requisition_id}`}>
                    <Eye className="w-4 h-4 mr-1" /> Cotações ({r.quotations?.length || 0})
                  </Button>
                  {r.status !== "converted" && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteReq(r)}><Trash2 className="w-4 h-4" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nova Requisição de Compra</DialogTitle></DialogHeader>
          <form onSubmit={createReq} className="space-y-3">
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <Select value={it.product_id} onValueChange={(v) => updateItem(i, "product_id", v)}>
                      <SelectTrigger><SelectValue placeholder="Produto (opcional)" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-3" placeholder="Descrição *" value={it.name} onChange={(e) => updateItem(i, "name", e.target.value)} />
                  <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="Qtd *" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} />
                  <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="Custo est." value={it.est_unit_cost} onChange={(e) => updateItem(i, "est_unit_cost", e.target.value)} />
                  <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, { product_id: "", name: "", quantity: "", est_unit_cost: "" }])}><Plus className="w-4 h-4 mr-1" /> Item</Button>
            <div className="space-y-1"><Label>Notas</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-requisition-btn">Criar Requisição</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Requisição {selected?.number} — Comparação de Cotações</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold mb-1">Itens</p>
                {(selected.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm border-b py-1">
                    <span>{it.name} × {it.quantity}</span>
                    <span className="font-mono text-muted-foreground">{formatCurrency(it.est_unit_cost)} /un</span>
                  </div>
                ))}
                <p className="text-right font-mono text-sm mt-1">Total estimado: <strong>{formatCurrency(selected.est_total)}</strong></p>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Cotações ({selected.quotations?.length || 0})</p>
                {(selected.quotations || []).map((q) => (
                  <div key={q.quotation_id} className={`flex items-center justify-between p-2 rounded border mb-1 ${q.total === bestQuote ? "border-emerald-500 bg-emerald-500/5" : ""}`} data-testid={`quotation-${q.quotation_id}`}>
                    <div>
                      <p className="font-medium text-sm">{q.supplier_name} {q.total === bestQuote && <Badge className="bg-emerald-500/20 text-emerald-600 ml-1">Melhor Preço</Badge>}</p>
                      <p className="text-xs text-muted-foreground">{q.delivery_days ? `Entrega em ${q.delivery_days} dia(s)` : "Sem prazo indicado"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold">{formatCurrency(q.total)}</p>
                      {selected.status !== "converted" && (
                        <Button size="sm" onClick={() => selectQuote(q)} data-testid={`select-quotation-${q.quotation_id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Selecionar
                        </Button>
                      )}
                      {selected.selected_quotation_id === q.quotation_id && <Badge className="bg-emerald-500/20 text-emerald-600">Vencedora</Badge>}
                    </div>
                  </div>
                ))}
                {(selected.quotations || []).length === 0 && <p className="text-sm text-muted-foreground">Ainda sem cotações</p>}
              </div>

              {selected.status !== "converted" && (
                <form onSubmit={addQuote} className="space-y-2 border-t pt-3">
                  <p className="text-sm font-semibold">Adicionar Cotação</p>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-5">
                      <Select value={quoteForm.supplier_id} onValueChange={(v) => setQuoteForm({ ...quoteForm, supplier_id: v })}>
                        <SelectTrigger data-testid="quote-supplier-select"><SelectValue placeholder="Fornecedor *" /></SelectTrigger>
                        <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-4" required type="number" step="0.01" min="0.01" placeholder="Total *" value={quoteForm.total} onChange={(e) => setQuoteForm({ ...quoteForm, total: e.target.value })} data-testid="quote-total-input" />
                    <Input className="col-span-3" type="number" min="0" placeholder="Prazo (dias)" value={quoteForm.delivery_days} onChange={(e) => setQuoteForm({ ...quoteForm, delivery_days: e.target.value })} />
                  </div>
                  <Button type="submit" size="sm" disabled={!quoteForm.supplier_id} data-testid="add-quotation-btn"><Plus className="w-4 h-4 mr-1" /> Adicionar Cotação</Button>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
